import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'
import AnimatedDotsCarousel from 'react-native-animated-dots-carousel'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { nextItemLabel, prevItemLabel } from '@lyrahealth-inc/shared-app-logic'

import { CarouselButton } from '../../atoms/carouselButton/CarouselButton'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { ButtonModifier } from '../../atoms/index'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface DotsPaginationProps {
  data: any[]
  currentIndex: number
  onNavigateToIndex: (index: number) => void
  customStyles?: {
    paginationContainer?: CSSObject
    paginationOuterContainer?: CSSObject
  }
}

const PaginationOuterContainer = styled.View<{
  customStyling?: CSSObject
}>(({ customStyling = {} }) => ({
  ...customStyling,
}))

const PaginationContainer = styled.View<{
  theme: ThemeType
  customStyling?: CSSObject
}>(({ theme: { breakpoints, spacing }, customStyling = {} }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  alignSelf: breakpoints.isMinWidthMobileM ? 'center' : 'auto',
  justifyContent: breakpoints.isMinWidthMobileM ? 'flex-start' : 'space-between',
  marginHorizontal: breakpoints.isMinWidthMobileM ? 0 : spacing['16px'],
  marginTop: breakpoints.isMobileSized ? spacing['24px'] : spacing['32px'],
  ...customStyling,
}))

const DotsContainer = styled.View<{
  theme: ThemeType
}>(({ theme: { breakpoints, spacing } }) => ({
  alignItems: 'center',
  width: breakpoints.isMinWidthMobileM ? '247px' : 'auto',
  marginHorizontal: breakpoints.isMinWidthMobileM ? spacing['8px'] : 0,
}))

export const DotsPagination: FunctionComponent<DotsPaginationProps> = ({
  data,
  currentIndex,
  onNavigateToIndex,
  customStyles,
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()

  return (
    <PaginationOuterContainer customStyling={customStyles?.paginationOuterContainer} testID={tID('DotsPagination')}>
      <PaginationContainer customStyling={customStyles?.paginationContainer}>
        <CarouselButton
          testID={tID('DotsPagination-leftButton')}
          accessibilityLabel={formatMessage(prevItemLabel)}
          leftIcon={<ChevronIcon direction={ChevronIconDirection.LEFT} size={26} />}
          onPress={() => onNavigateToIndex(currentIndex - 1)}
          disabled={currentIndex === 0}
          modifier={ButtonModifier.FILLED}
        />

        <DotsContainer>
          <AnimatedDotsCarousel
            length={data.length}
            currentIndex={currentIndex}
            maxIndicators={data.length <= 5 ? 5 : 3}
            activeIndicatorConfig={{
              color: colors.components.dotsPagination.activeDot.fill,
              margin: 4,
              opacity: 1,
              size: 8,
            }}
            inactiveIndicatorConfig={{
              color: colors.components.dotsPagination.inactiveDot.fill,
              margin: 4,
              opacity: 1,
              size: 8,
            }}
            decreasingDots={[
              {
                config: {
                  color: colors.components.dotsPagination.inactiveDot.fill,
                  margin: 4,
                  opacity: 1,
                  size: 6,
                },
                quantity: 1,
              },
              {
                config: {
                  color: colors.components.dotsPagination.inactiveDot.fill,
                  margin: 4,
                  opacity: 1,
                  size: 4,
                },
                quantity: 1,
              },
            ]}
          />
        </DotsContainer>

        <CarouselButton
          testID={tID('DotsPagination-rightButton')}
          accessibilityLabel={formatMessage(nextItemLabel)}
          leftIcon={<ChevronIcon direction={ChevronIconDirection.RIGHT} size={26} />}
          onPress={() => onNavigateToIndex(currentIndex + 1)}
          disabled={currentIndex === data.length - 1}
          modifier={ButtonModifier.FILLED}
        />
      </PaginationContainer>
    </PaginationOuterContainer>
  )
}
